import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { ErrorType } from 'state/custom-instance';
import type {
  OrganisationAdminDetails,
  User,
} from 'state/__generated__/tadaahAPI';
import {
  useApiMyselfRead,
  useApiOrganisationAdminList,
} from 'state/__generated__/tadaahAPI';
import useIsFreelancerLoggedIn from 'hooks/use-is-freelancer-logged-in';

export type OrganisationContextType = {
  user: {
    data: User | undefined;
    isLoading?: boolean;
    error: ErrorType<unknown> | null;
  };
  organisations: {
    organisations: OrganisationAdminDetails[] | undefined;
    selectedOrganisationId: number | undefined;
    setSelectedOrganisationId: React.Dispatch<
      React.SetStateAction<number | undefined>
    >;
    isLoading: boolean;
  };
};

const organisationSettingsContextInitialValue: OrganisationContextType = {
  organisations: {
    isLoading: true,
    organisations: [],
    selectedOrganisationId: undefined,
    setSelectedOrganisationId: () => undefined,
  },
  user: {
    data: undefined,
    error: null,
    isLoading: false,
  },
};

export const OrganisationCtx = createContext<OrganisationContextType>(
  organisationSettingsContextInitialValue,
);

const OrganisationContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const localStorageOrgId = localStorage.getItem('selectedOrganisationId');
  const initialOrgId = localStorageOrgId
    ? parseInt(localStorageOrgId, 10)
    : undefined;

  const [selectedOrganisationId, setSelectedOrganisationId] = useState<
    number | undefined
  >(initialOrgId);

  const isLoggedIn = useIsFreelancerLoggedIn();
  const {
    data: userData,
    error: userError,
    isLoading: userIsLoading,
  } = useApiMyselfRead({
    query: {
      enabled: isLoggedIn,
      queryKey: 'user-profile',
    },
  });

  const {
    data: organisationAdminData,
    isLoading: organisationAdminDataIsLoading,
  } = useApiOrganisationAdminList({
    query: {
      enabled: isLoggedIn,
      queryKey: ['organisation-admin-list'],
    },
  });

  // If nothing in localStorage, set selected org as first of the list
  useEffect(() => {
    if (!localStorageOrgId) {
      if (
        organisationAdminData?.data?.length &&
        organisationAdminData.data[0].id
      ) {
        setSelectedOrganisationId(organisationAdminData.data[0].id);
      }
    }
  }, [localStorageOrgId, organisationAdminData]);

  // if the localStorageOrgId is not included in the organisationAdminData, remove the localStorageOrgId
  // this can happen when user switches account. In that case, the new user might not be admin of the localStorageOrgId organisation.
  useEffect(() => {
    if (localStorageOrgId && organisationAdminData?.data?.length) {
      if (
        !organisationAdminData.data.some(
          (org) => org.id === Number(localStorageOrgId),
        )
      ) {
        setSelectedOrganisationId(undefined);
        localStorage.removeItem('selectedOrganisationId');
      }
    }
  }, [localStorageOrgId, organisationAdminData]);

  // set the selectedOrganisationId in localStorage if selectedOrganisationId changes.
  useEffect(() => {
    if (selectedOrganisationId !== undefined) {
      localStorage.setItem(
        'selectedOrganisationId',
        selectedOrganisationId.toString(),
      );
    }
  }, [selectedOrganisationId]);

  const organisationContext: OrganisationContextType = {
    organisations: {
      isLoading: organisationAdminDataIsLoading,
      organisations: organisationAdminData?.data,
      selectedOrganisationId,
      setSelectedOrganisationId,
    },
    user: {
      data: userData?.data,
      error: userError,
      isLoading: userIsLoading,
    },
  };

  return (
    <OrganisationCtx.Provider value={organisationContext}>
      {children}
    </OrganisationCtx.Provider>
  );
};

export const useOrganisationContext = (): OrganisationContextType =>
  useContext(OrganisationCtx);

export default {
  Consumer: OrganisationCtx.Consumer,
  Provider: OrganisationContextProvider,
};
